/**
 * Where an entity lives (#98, design.functional §6): every entity title deep-links to that
 * entity's record — its drawer on its home list (`/registry?type=initiative&open=INI-0006`),
 * or its own page where the record has one. Never a bare list.
 *
 * Pure, so the rule "no link lands on a bare list" is testable without a router.
 */

export interface EntityRef {
  /** entity_type.code */
  type: string;
  id?: string | null;
  /** The human key (INI-0006); preferred in URLs because people read and paste them. */
  key?: string | null;
}

/** Types whose record is a page of its own rather than a drawer on the registry. */
const PAGE_ROUTES: Record<string, (id: string) => string> = {
  // G23 (#161): the merged Requests page lives at the top level, not under /governance.
  request: (id) => `/requests/${encodeURIComponent(id)}`,
  document: (id) => `/library/${encodeURIComponent(id)}`,
};

/** Types that are not records a person opens (a user is a person, a section lives in its document). */
const NOT_LINKED = new Set(['user', 'section']);

/** The URL of an entity's record, or null when it has no record of its own to open. */
export function entityHref(ref: EntityRef): string | null {
  const handle = ref.key || ref.id;
  if (!ref.type || !handle || NOT_LINKED.has(ref.type)) return null;
  // G56 (#482): a proposal opens in the Proposals list's drawer, keyed by its human key.
  if (ref.type === 'proposal') return `/governance/proposals?${new URLSearchParams({ open: handle }).toString()}`;
  const page = PAGE_ROUTES[ref.type];
  if (page) return ref.id ? page(ref.id) : null;
  const params = new URLSearchParams({ type: ref.type, open: handle });
  return `/registry?${params.toString()}`;
}

/** The registry list for a type, optionally with one record open. */
export function registryHref(type: string, open?: string | null): string {
  const params = new URLSearchParams({ type });
  if (open) params.set('open', open);
  return `/registry?${params.toString()}`;
}

/** The full-page form of a record ("Open as page"): /registry/:type/:key. */
export function recordPageHref(ref: EntityRef): string {
  return `/registry/${encodeURIComponent(ref.type)}/${encodeURIComponent(ref.key || ref.id || '')}`;
}
